import {commonControllers} from '@mintdata-wsp/common/controllers/CommonControllers';
import {AbstractSheetSettingsController} from '@mintdata-wsp/common/controllers/spreadsheet/AbstractSheetSettingsController';
import {MeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/MeshCellValueFormat';
import {IMeshCellValueFormat} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/cellFormat/valueFormat/ValueFormat';
import {MeshCellNote} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellNote';
import {MeshCellSettings} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshCellSettings';
import {MeshLocation, MeshLocationKey} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshLocation';
import {MeshRange} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshRange';
import {MeshSheetSize} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/MeshSheetSize';
import {Sheet} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/Sheet';
import {SheetSettings} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetSettings';
import {SheetSettingsId} from '@mintdata-wsp/common/state/ui/visualDesigner/spreadsheet/SheetSettingsTypes';
import {MeshUtils} from '@mintdata-wsp/common/util/MeshUtils';
import {SheetId, SlotId} from '@mintdata/mint-kit/api/models/Identifiers';
import {list, optional, primitive, raw, serializable} from '@mintdata/mint-kit/impl/util/serializr/src/serializr';
import {action} from 'mobx';

export class AppliedSheetSettings
{
    @serializable
    public id:SheetSettingsId;
    @serializable
    public sheetId:SheetId;
    @serializable(optional(primitive()))
    public slotId:SlotId | null;
    @serializable(list(raw()))
    public cellSettings:MeshCellSettings[];
    @serializable(list(raw()))
    public cellNotes:MeshCellNote[];

    public constructor(id:SheetSettingsId,
                       sheetId:SheetId,
                       slotId:SlotId | null,
                       cellSettings:MeshCellSettings[],
                       cellNotes:MeshCellNote[])
    {
        this.id = id;
        this.sheetId = sheetId;
        this.slotId = slotId;
        this.cellSettings = cellSettings;
        this.cellNotes = cellNotes;
    }
}

export class SheetSettingsController extends AbstractSheetSettingsController
{
    @action
    public createSheetSettings(sheetId:SheetId):SheetSettings
    {
        const {main} = commonControllers.spreadsheets;
        const sheet:Sheet = this._findAnySheetById(sheetId);

        const sheetSettings = this.getSheetSettings(sheetId) as SheetSettings | null;

        if( !sheetSettings )
        {
            const workbook = main.findWorkbookById(sheet.workbook.id);
            workbook.view.addSheetSettings(SheetSettings.create(sheetId, sheetId, null));

            return workbook.view.getSheetSettingsById(sheetId);
        }

        return sheetSettings;
    }

    public getCellSettings(sheetId:SheetId, cellKey:MeshLocationKey):MeshCellSettings | null
    {
        const sheetSettings = this.getSheetSettings(sheetId) as SheetSettings;

        return this._getCellSettings(sheetSettings, cellKey);
    }

    public getCellSettingsBySheetSettingsId(sheetSettingsId:SheetSettingsId, cellKey:MeshLocationKey):MeshCellSettings | null
    {
        const sheetSettings = this._getSheetSettings(sheetSettingsId);

        // This check shouldn't be here. It needs to be removed at the right time. Take a look at the comment here for more context https://minter.atlassian.net/browse/MAM-2117
        if( !sheetSettings )
            return null;

        return this._getCellSettings(sheetSettings, cellKey);
    }

    public getCellNote(sheetId:SheetId, cellKey:MeshLocationKey):MeshCellNote | null
    {
        const sheetSettings = this.getSheetSettings(sheetId) as SheetSettings;

        return this._getCellNote(sheetSettings, cellKey);
    }

    public getCellNoteBySheetSettingsId(sheetSettingsId:SheetSettingsId, cellKey:MeshLocationKey):MeshCellNote | null
    {
        const sheetSettings = this._getSheetSettings(sheetSettingsId);

        return this._getCellNote(sheetSettings, cellKey);
    }

    @action
    public setCellSettings(sheetId:SheetId, cellKey:MeshLocationKey, cellSettings:MeshCellSettings):void
    {
        const sheetSettings = this.getOrCreateSheetSettings(sheetId) as SheetSettings;

        if( cellSettings )
        {
            const {main} = commonControllers.spreadsheets;

            const sheet:Sheet = this._findAnySheetById(sheetId);
            const workbook = main.findWorkbookById(sheet.workbook.id);

            workbook.view.addCellSettings(cellSettings);
            sheetSettings.setCellSettings(cellKey, cellSettings.id);
        }
        else
            sheetSettings.removeCellSettings(cellKey);
    }

    @action
    public setCellNote(sheetId:SheetId, cellKey:MeshLocationKey, cellNote:MeshCellNote):void
    {
        const {main} = commonControllers.spreadsheets;

        const sheetSettings = this.getOrCreateSheetSettings(sheetId) as SheetSettings;
        const sheet:Sheet = this._findAnySheetById(sheetId);
        const workbook = main.findWorkbookById(sheet.workbook.id);

        if( cellNote )
        {
            workbook.view.addCellNote(cellNote);
            sheetSettings.setCellNote(cellKey, cellNote.id);
        }
        else
        {
            workbook.view.removeCellNote(sheetSettings.getCellNote(cellKey));
            sheetSettings.removeCellNote(cellKey);
        }
    }

    public getCellFormat(sheetId:SheetId, cellKey:MeshLocationKey):IMeshCellValueFormat | null
    {
        const sheetSettings = this.getSheetSettings(sheetId) as SheetSettings;

        return this._getCellFormat(sheetSettings, cellKey);
    }

    public getCellFormatBySheetSettingsId(sheetSettingsId:SheetSettingsId, cellKey:MeshLocationKey):IMeshCellValueFormat | null
    {
        const sheetSettings = this._getSheetSettings(sheetSettingsId) as SheetSettings;

        if( !sheetSettings )
            return null;

        return this._getCellFormat(sheetSettings, cellKey);
    }

    @action
    public setCellFormat(sheetId:SheetId, cellRange:MeshRange, sheetSize:MeshSheetSize, formatId:string):void
    {
        const sheetSettings = this.getSheetSettings(sheetId) as SheetSettings;

        MeshUtils.eachCellInRange(cellRange, sheetSize.numRows, sheetSize.numCols, (cellKey:string) =>
        {
            if( formatId )
                sheetSettings.setCellFormat(cellKey, formatId);
            else
                sheetSettings.removeCellFormat(cellKey);
        });
    }

    @action
    public removeCellFormat(sheetId:SheetId, cellRange:MeshRange):void
    {
        const sheetSettings = this.getSheetSettings(sheetId) as SheetSettings;

        MeshUtils.eachCellInRange(cellRange, cellRange.rowsCount, cellRange.columnsCount, (cellKey:string) => (
            sheetSettings.removeCellFormat(cellKey)
        ));
    }

    @action
    public removeSheetSettingsForCell(sheetId:SheetId, location:MeshLocation):void
    {
        const appliedSheetSettings = this.getAppliedSheetSettings(sheetId, location);

        appliedSheetSettings.forEach(value =>
        {
            if( value.slotId === null )
            {
                const topSheetSettings = this.getSheetSettings(value.id) as SheetSettings;
                if( topSheetSettings )
                {
                    const cellKey = location.asKey();

                    topSheetSettings.removeCellSettings(cellKey);
                    this.setCellNote(sheetId, cellKey, null);
                }
            }
            else
                this._removeSheetSettings(value.id);
        });
    }

    public getAppliedSheetSettings(sheetId:SheetId, location:MeshLocation):AppliedSheetSettings[]
    {
        const result:AppliedSheetSettings[] = [];

        const {main, sheet} = commonControllers.spreadsheets;
        const anySheet:Sheet = this._findAnySheetById(sheetId);
        const workbook = main.findWorkbookById(anySheet.workbook.id);

        if( workbook )
        {
            const settings = workbook.view.getSheetSettingsById(this.buildSheetSettingsIdBySheetId(sheetId));

            if( settings )
            {
                const cellSettings = this.getCellSettingsBySheetSettingsId(settings.id, location.asKey());
                const cellNote = this.getCellNoteBySheetSettingsId(settings.id, location.asKey());

                result.push(new AppliedSheetSettings(
                    settings.id,
                    settings.sheetId,
                    null,
                    cellSettings ? [cellSettings] : [],
                    cellNote ? [cellNote] : []
                ));
            }
        }

        const slot = sheet.findSlot(anySheet.id, location.toSlotLocation());

        if( slot )
        {
            workbook.view.sheetSettings.forEach(settings =>
            {
                if( settings.slotId === slot.id )
                {
                    const cellSettingsList:MeshCellSettings[] = [];

                    settings.cellSettings.forEach((value, key) => (
                        cellSettingsList.push(workbook.view.cellSettings.get(value))
                    ));

                    const cellNotesList:MeshCellNote[] = [];

                    settings.cellNotes.forEach((value, key) => (
                        cellNotesList.push(workbook.view.cellNotes.get(value))
                    ));

                    result.push(new AppliedSheetSettings(
                        settings.id,
                        settings.sheetId,
                        settings.slotId,
                        cellSettingsList,
                        cellNotesList
                    ));
                }
            });
        }

        return result;
    }

    protected _getSheetSettings(sheetSettingsId:SheetSettingsId):SheetSettings | null
    {
        const {main} = commonControllers.spreadsheets;

        const allWorkbookIds = main.getAllWorkbookIds();

        for( let workbookId of allWorkbookIds )
        {
            const workbook = main.findWorkbookById(workbookId);
            const sheetSettings = workbook.view.getSheetSettingsById(sheetSettingsId);

            if( sheetSettings )
                return sheetSettings;
        }

        return null;
    }

    @action
    protected _removeSheetSettings(sheetSettingsId:SheetSettingsId):void
    {
        const {main} = commonControllers.spreadsheets;

        const allWorkbookIds = main.getAllWorkbookIds();

        for( let workbookId of allWorkbookIds )
        {
            const workbook = main.findWorkbookById(workbookId);
            workbook.view.deleteSheetSettings(sheetSettingsId);
        }
    }

    protected _findAnySheetById(sheetId:SheetId):Sheet
    {
        const {main} = commonControllers.spreadsheets;

        return main.findAnySheetById(sheetId) as Sheet;
    }

    private _getCellFormat(sheetSettings:SheetSettings, cellKey:MeshLocationKey):IMeshCellValueFormat | null
    {
        const cellFormatsId = sheetSettings.getCellFormat(cellKey);

        if( !cellFormatsId )
            return MeshCellValueFormat.General;

        if( MeshCellValueFormat.BuiltInFormats[cellFormatsId] )
            return MeshCellValueFormat.BuiltInFormats[cellFormatsId];

        // todo: support for non built-in formats
        return null;
    }
}
